import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type RefObject,
} from 'react';

/**
 * Options for {@link useInfiniteSchedule}.
 */
export interface UseInfiniteScheduleOptions {
  /** Anchor date the feed initially renders around (typically selectedDate or today). */
  anchorDate: Date;
  /** Days back of anchorDate to render on mount. Default: 7. */
  initialBackDays?: number;
  /** Days forward of anchorDate to render on mount. Default: 14. */
  initialForwardDays?: number;
  /** Step size when a sentinel fires. Default: 7. */
  stepDays?: number;
  /**
   * Cap on `visibleDays.length`. When an extend pushes the window past
   * this cap, the opposite side is trimmed by the same number of days,
   * preserving the direction the user is scrolling (extending forward
   * → trim from the top; extending back → trim from the bottom). Top
   * trims preserve scroll anchor via the same `useLayoutEffect` snapshot
   * pattern as prepends, but in reverse: snapshot `scrollHeight` before
   * commit, then `scrollTop -= (oldHeight - newHeight)` after commit.
   *
   * Default: `Infinity` — trim is OPT-IN. The infrastructure ships
   * dormant because top trim runs the same anchor-preservation logic as
   * extendBack; in pathological cases (e.g. an extendBack and a forward
   * trim landing in the same render) the two anchor adjustments could
   * fight. Bottom trim is always safe (scrollTop is unaffected). Tests
   * exercise the policy with a finite cap; consumers can opt in by
   * passing one.
   */
  maxVisibleDays?: number;
  /**
   * Hard floor for the window: days before this date are never rendered.
   * Clamps the initial window and `jumpTo`, and turns the top sentinel
   * into a no-op (no fetch, no spinner) once the window starts here.
   * The Schedule feed passes the profile-timezone "today" so the feed
   * cannot scroll into the past — history lives in the History page.
   * Pass a live value (derived from useTodayKey) and the floor advances
   * at midnight; already-rendered days are not retro-trimmed when the
   * floor moves, they age out on the next jump/reload.
   */
  minDate?: Date | null;
  /** Window-hook action: load more past data. Must reject on failure. */
  extendBack: (days: number) => Promise<void>;
  /** Window-hook action: load more future data. Must reject on failure. */
  extendForward: (days: number) => Promise<void>;
  /**
   * Ref to the scroll container element. Required so we can read scrollHeight
   * and adjust scrollTop synchronously on prepend. If the container is null
   * at runtime OR it doesn't itself scroll (i.e. `scrollHeight === clientHeight`,
   * the page scrolls the viewport instead), the hook falls back to
   * `document.scrollingElement` (the spec-correct page scroller) and
   * observes the viewport (`root: null`).
   */
  scrollContainerRef: RefObject<HTMLElement | null>;
}

/**
 * Result returned by {@link useInfiniteSchedule}.
 */
export interface UseInfiniteScheduleResult {
  /** Days currently rendered, ascending, with no gaps. */
  visibleDays: Date[];
  /** Attach to the topmost element of the feed; triggers prepend on intersect. */
  topSentinelRef: (node: HTMLDivElement | null) => void;
  /** Attach to the bottommost element of the feed; triggers append on intersect. */
  bottomSentinelRef: (node: HTMLDivElement | null) => void;
  /** True while a top- or bottom-extend is in flight. For aria-busy on sentinels. */
  isExtending: boolean;
  /** Programmatically re-center on a new date (e.g., user pressed Today). */
  jumpTo: (date: Date) => void;
}

const DEFAULT_INITIAL_BACK = 7;
const DEFAULT_INITIAL_FORWARD = 14;
const DEFAULT_STEP = 7;

/** Strip a Date to local midnight without mutating the input. */
function toMidnight(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

/** Add N days to a Date and return a new Date at local midnight. */
function addDays(date: Date, days: number): Date {
  const next = toMidnight(date);
  next.setDate(next.getDate() + days);
  return next;
}

/** Clamp `date` up to `floor` (compared at local midnight) when a floor is set. */
function clampToFloor(date: Date, floor: Date | null | undefined): Date {
  if (!floor) return date;
  const f = toMidnight(floor);
  return date.getTime() < f.getTime() ? f : date;
}

/** Build a consecutive Date[] from `from` to `to` inclusive, ascending. */
function rangeDays(from: Date, to: Date): Date[] {
  const start = toMidnight(from);
  const end = toMidnight(to);
  const out: Date[] = [];
  const cursor = new Date(start);
  while (cursor.getTime() <= end.getTime()) {
    out.push(new Date(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }
  return out;
}

/**
 * Owns the visible-day window and intersection sentinels for the infinite
 * Schedule feed. Coordinates with `useScheduleWindow` (via `extendBack` /
 * `extendForward`) so data is fetched before the corresponding day card
 * mounts.
 *
 * Critical properties enforced here:
 *  - Prepends preserve scroll anchor synchronously in `useLayoutEffect`.
 *  - A re-entry guard (`isLoadingRef`) prevents overlapping extends.
 *  - The top sentinel does NOT fire on initial mount even if already in
 *    the viewport — we wait until the user has actually scrolled, so the
 *    feed doesn't immediately expand on first paint.
 *  - On a failed extend, `visibleDays` is left untouched. The new days
 *    are only committed after the await resolves.
 */
export function useInfiniteSchedule(
  options: UseInfiniteScheduleOptions,
): UseInfiniteScheduleResult {
  const {
    anchorDate,
    initialBackDays = DEFAULT_INITIAL_BACK,
    initialForwardDays = DEFAULT_INITIAL_FORWARD,
    stepDays = DEFAULT_STEP,
    maxVisibleDays = Infinity,
    minDate = null,
    extendBack,
    extendForward,
    scrollContainerRef,
  } = options;

  const [visibleDays, setVisibleDays] = useState<Date[]>(() => {
    const from = clampToFloor(addDays(anchorDate, -initialBackDays), minDate);
    const to = addDays(anchorDate, initialForwardDays);
    return rangeDays(from, to.getTime() < from.getTime() ? from : to);
  });
  const [isExtending, setIsExtending] = useState(false);

  // Re-entry guard. Synchronous (ref, not state) because an
  // IntersectionObserver entry can fire again before React commits a
  // setIsExtending(true).
  const isLoadingRef = useRef(false);

  // Monotonic generation counter. Bumped whenever `jumpTo` re-centers the
  // window. An in-flight `handleExtend` captures the value at entry and
  // aborts its commit if the generation has changed by the time the
  // extend's await resolves — otherwise stale past/future days would get
  // grafted onto a window that no longer corresponds to them.
  const generationRef = useRef(0);

  // Anchor preservation: when we prepend, we snapshot scrollHeight before
  // committing the new days, then bump scrollTop by the diff in a
  // useLayoutEffect that runs after React paints the new nodes but
  // before the browser pixels them.
  const pendingPrependRef = useRef<{ oldScrollHeight: number } | null>(null);
  // Top-trim mirror of the prepend snapshot. When extendForward causes
  // a top trim, we snapshot the old scrollHeight here and the
  // useLayoutEffect subtracts the (negative) height delta from
  // scrollTop so the visible content stays put. Bottom trims (from
  // extendBack) don't need anchoring — scrollTop is unaffected by
  // removing content below the viewport.
  const pendingTopTrimRef = useRef<{ oldScrollHeight: number } | null>(null);

  /** Stable maxVisibleDays mirror for the async handleExtend body. */
  const maxVisibleDaysRef = useRef(maxVisibleDays);
  maxVisibleDaysRef.current = maxVisibleDays;

  // Stable snapshot of `visibleDays` for the async `handleExtend` body
  // so it can decide whether a forthcoming setState will trip the trim
  // cap before React commits. Updated synchronously after every render
  // by a useLayoutEffect below. Declared up here so the hook-call order
  // is fixed across renders.
  const visibleDaysRef = useRef<Date[]>(visibleDays);

  const topObserverRef = useRef<IntersectionObserver | null>(null);
  const bottomObserverRef = useRef<IntersectionObserver | null>(null);
  const topNodeRef = useRef<HTMLDivElement | null>(null);

  // IntersectionObserver reports CHANGES. After a jump the sentinel is
  // already inside the viewport (the jump target is the first day), and
  // after a prepend it may still be — short, empty days do not push it
  // past the 400px margin. Neither is a change, so neither fires. Set
  // this flag and a layout effect below re-observes the sentinel once
  // the window has committed, which makes the browser deliver the
  // current intersection again: an upward fill that repeats until the
  // sentinel is out of range or the window is at the floor (YAR-470).
  const rearmTopRef = useRef(false);

  /**
   * Resolve the element that actually scrolls. Mirrors `resolveRoot`'s
   * logic, but returns a concrete HTMLElement (never null) because the
   * caller needs to read/write `scrollHeight` and `scrollTop`.
   *
   * Production case: the page is the scroller. The inner `scrollContainerRef`
   * div has no overflow (its `scrollHeight === clientHeight`), so reading
   * its scrollTop is a no-op and writes are silently dropped. We must
   * fall through to `document.scrollingElement` (the spec-correct page
   * scroll target) — usually `<html>` in standards mode. Older browsers
   * may not expose `scrollingElement`, in which case `documentElement`
   * is a safe fallback.
   */
  const resolveScroller = useCallback((): HTMLElement => {
    const node = scrollContainerRef.current;
    if (node && node.scrollHeight > node.clientHeight) return node;
    return (
      (document.scrollingElement as HTMLElement | null) ??
      document.documentElement
    );
  }, [scrollContainerRef]);

  // Latest callbacks/values exposed to async observer entries via refs so
  // the observer doesn't have to be torn down on every render.
  const extendBackRef = useRef(extendBack);
  extendBackRef.current = extendBack;
  const extendForwardRef = useRef(extendForward);
  extendForwardRef.current = extendForward;
  const stepDaysRef = useRef(stepDays);
  stepDaysRef.current = stepDays;
  const minDateRef = useRef(minDate);
  minDateRef.current = minDate;
  // Gate the top sentinel: only honor intersections after the user has
  // scrolled at least once. Otherwise on initial mount, if the top
  // sentinel happens to be inside the viewport, it would fire and
  // prepend before the user has even seen the feed. A `jumpTo` that
  // lands above the floor also opens it (see there): the days between
  // the floor and the jump are only reachable by prepending, and a user
  // sitting at the very top cannot produce the scroll that would
  // otherwise open it.
  const hasScrolledRef = useRef(false);
  useEffect(() => {
    const onScroll = () => {
      hasScrolledRef.current = true;
    };
    // We listen on the container first; if it doesn't scroll (no
    // overflow), the window will. Attach to both — whichever fires
    // first flips the flag.
    const container = scrollContainerRef.current;
    container?.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      container?.removeEventListener('scroll', onScroll);
      window.removeEventListener('scroll', onScroll);
    };
  }, [scrollContainerRef]);

  /** Internal: extend the window. Direction-aware. */
  const handleExtend = useCallback(
    async (direction: 'back' | 'forward') => {
      if (isLoadingRef.current) return;
      // Floor check BEFORE arming the guard/spinner: when the window
      // already starts at `minDate` there is nothing behind it — the
      // Schedule feed's backward limit is today, so the intersection is
      // a no-op (no fetch, no "Loading more…" flash).
      if (direction === 'back' && minDateRef.current) {
        const floor = toMidnight(minDateRef.current);
        const oldest = visibleDaysRef.current[0];
        if (!oldest || oldest.getTime() <= floor.getTime()) return;
      }
      isLoadingRef.current = true;
      setIsExtending(true);

      // Capture the generation BEFORE the await. If `jumpTo` is called
      // while we're suspended, the captured value will lag the live one
      // and we know the commit is stale.
      const gen = generationRef.current;
      const step = stepDaysRef.current;

      try {
        if (direction === 'back') {
          // Snapshot BEFORE the await + setState so anchor restoration
          // has the right baseline. The actual scrollTop adjustment
          // happens in useLayoutEffect below. Use the same resolver as
          // the layout effect so both sides agree on which element is
          // the scroller (inner div vs. page).
          const container = resolveScroller();
          pendingPrependRef.current = {
            oldScrollHeight: container.scrollHeight,
          };

          // Await BEFORE mutating visibleDays so a rejection leaves us
          // unchanged.
          await extendBackRef.current(step);

          // Aborted by jumpTo while we were awaiting. The window has
          // been re-centered; grafting these days would jam stale data
          // onto an unrelated window. `jumpTo` already cleared the
          // pending snapshot so the layout effect won't run.
          if (gen !== generationRef.current) return;

          // The sentinel may still be in range once these days land;
          // have it report again so the fill continues to the floor.
          rearmTopRef.current = true;
          setVisibleDays((prev) => {
            if (prev.length === 0) return prev;
            const oldest = prev[0];
            // Clamp the prepend at the floor. Re-check the "already at
            // the floor" case here because `prev` may differ from the
            // pre-await snapshot.
            const floor = minDateRef.current
              ? toMidnight(minDateRef.current)
              : null;
            if (floor && oldest.getTime() <= floor.getTime()) return prev;
            const newDays = rangeDays(
              clampToFloor(addDays(oldest, -step), floor),
              addDays(oldest, -1),
            );
            const next = [...newDays, ...prev];
            // LRU trim: extended BACK → trim from the BOTTOM. Bottom
            // trim does not need anchor preservation because removing
            // content below the viewport leaves scrollTop unaffected.
            const cap = maxVisibleDaysRef.current;
            if (Number.isFinite(cap) && next.length > cap) {
              return next.slice(0, cap);
            }
            return next;
          });
        } else {
          await extendForwardRef.current(step);

          if (gen !== generationRef.current) return;

          // Determine whether a top trim will fire so we can snapshot
          // scrollHeight BEFORE committing the new days. The trim has
          // to happen inside the same setVisibleDays callback to avoid
          // a flicker, so we compute the post-commit length here from
          // the current `visibleDays` state. This is a best-effort
          // snapshot — if `visibleDays` differs by the time React
          // applies the updater, the layout effect's diff just becomes
          // a no-op (newHeight === oldHeight). The setter below is the
          // source of truth for whether trim actually happened.
          const cap = maxVisibleDaysRef.current;
          const willTrim =
            Number.isFinite(cap) && visibleDaysRef.current.length + step > cap;
          if (willTrim) {
            const scroller = resolveScroller();
            pendingTopTrimRef.current = {
              oldScrollHeight: scroller.scrollHeight,
            };
          }

          setVisibleDays((prev) => {
            if (prev.length === 0) return prev;
            const newest = prev[prev.length - 1];
            const newDays = rangeDays(
              addDays(newest, 1),
              addDays(newest, step),
            );
            const next = [...prev, ...newDays];
            // LRU trim: extended FORWARD → trim from the TOP. The
            // useLayoutEffect below restores scrollTop using the
            // snapshot we took above.
            if (Number.isFinite(cap) && next.length > cap) {
              return next.slice(next.length - cap);
            }
            // No trim happened — drop the speculative snapshot so the
            // layout effect doesn't see a stale one.
            if (willTrim) pendingTopTrimRef.current = null;
            return next;
          });
        }
      } catch {
        // On failure, drop the pending prepend snapshot — no anchor
        // adjustment is needed since visibleDays didn't change.
        if (direction === 'back') pendingPrependRef.current = null;
        else pendingTopTrimRef.current = null;
      } finally {
        isLoadingRef.current = false;
        setIsExtending(false);
      }
    },
    [resolveScroller],
  );

  // Mirror visibleDays into a ref so async code (handleExtend) can read
  // the latest length without retriggering a re-bind. We update inside
  // a layout effect so the value matches what's in the DOM by the time
  // any post-commit callbacks read it.
  useLayoutEffect(() => {
    visibleDaysRef.current = visibleDays;
  }, [visibleDays]);

  // Apply scroll anchor restoration synchronously after a prepend OR
  // top-trim lands. Must be useLayoutEffect (NOT useEffect, NOT rAF) so
  // the browser paints the corrected scrollTop in the same frame as
  // the new DOM — otherwise the user sees a flash of jump.
  //
  // Prepend: scrollHeight GREW. Bump scrollTop by (new - old) so the
  // viewport-relative position of the previously-topmost row is
  // preserved.
  //
  // Top trim: scrollHeight SHRANK (old > new). Subtract that delta
  // from scrollTop so the row the user was looking at stays put. The
  // two snapshots are mutually exclusive in normal operation — top
  // trim only fires on extendForward, prepend only on extendBack — so
  // we handle them in sequence. If both somehow landed in the same
  // commit, prepend's positive diff and trim's negative diff would
  // sum, which is still the correct adjustment.
  useLayoutEffect(() => {
    const container = resolveScroller();
    const prepend = pendingPrependRef.current;
    const trim = pendingTopTrimRef.current;
    if (!prepend && !trim) return;
    const newScrollHeight = container.scrollHeight;
    if (prepend) {
      const diff = newScrollHeight - prepend.oldScrollHeight;
      if (diff > 0) container.scrollTop += diff;
      pendingPrependRef.current = null;
    }
    if (trim) {
      const diff = trim.oldScrollHeight - newScrollHeight;
      if (diff > 0)
        container.scrollTop = Math.max(0, container.scrollTop - diff);
      pendingTopTrimRef.current = null;
    }
  }, [visibleDays, resolveScroller]);

  // Runs after the anchor restoration above (declared later, same
  // dependency), so the re-reported intersection is measured against the
  // settled scroll position.
  useLayoutEffect(() => {
    if (!rearmTopRef.current) return;
    rearmTopRef.current = false;
    const observer = topObserverRef.current;
    const node = topNodeRef.current;
    if (!observer || !node) return;
    observer.unobserve(node);
    observer.observe(node);
  }, [visibleDays]);

  // ---- Sentinel observers ----
  //
  // Each sentinel uses its own observer so we can tear down/re-attach
  // independently when the node changes (callback refs). The observer's
  // root is the scroll container if it scrolls internally, or the
  // viewport (root: null) otherwise.

  const resolveRoot = useCallback((): Element | null => {
    const node = scrollContainerRef.current;
    if (!node) return null;
    // If the container itself scrolls (overflow-y auto/scroll and
    // scrollHeight > clientHeight), use it as root. Otherwise the
    // viewport is what scrolls — return null.
    if (node.scrollHeight > node.clientHeight) return node;
    return null;
  }, [scrollContainerRef]);

  const topSentinelRef = useCallback(
    (node: HTMLDivElement | null) => {
      topObserverRef.current?.disconnect();
      topObserverRef.current = null;
      topNodeRef.current = node;
      if (!node) return;
      const observer = new IntersectionObserver(
        (entries) => {
          // Only honor entries that are actively intersecting AND only
          // after the user has scrolled at least once. The latter
          // prevents an initial-mount prepend when the sentinel
          // happens to be inside the viewport.
          if (!hasScrolledRef.current) return;
          for (const entry of entries) {
            if (entry.isIntersecting) {
              void handleExtend('back');
              return;
            }
          }
        },
        {
          root: resolveRoot(),
          rootMargin: '400px 0px',
          threshold: 0,
        },
      );
      observer.observe(node);
      topObserverRef.current = observer;
    },
    [handleExtend, resolveRoot],
  );

  const bottomSentinelRef = useCallback(
    (node: HTMLDivElement | null) => {
      bottomObserverRef.current?.disconnect();
      bottomObserverRef.current = null;
      if (!node) return;
      const observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              void handleExtend('forward');
              return;
            }
          }
        },
        {
          root: resolveRoot(),
          rootMargin: '400px 0px',
          threshold: 0,
        },
      );
      observer.observe(node);
      bottomObserverRef.current = observer;
    },
    [handleExtend, resolveRoot],
  );

  useEffect(() => {
    return () => {
      topObserverRef.current?.disconnect();
      bottomObserverRef.current?.disconnect();
    };
  }, []);

  /**
   * Re-center the feed around a new date. Replaces `visibleDays`
   * with a fresh ±days window; does NOT auto-restore scroll
   * (Phase 4 will handle scroll-into-view).
   */
  const jumpTo = useCallback(
    (date: Date) => {
      // Bump the generation so any in-flight `handleExtend` that hasn't
      // resolved its await yet will detect the change and skip its
      // setVisibleDays commit. Without this, a slow extendBack/Forward
      // that was kicked off against the OLD window would graft stale
      // edge days onto the brand new window the moment it resolves.
      generationRef.current += 1;
      // Release the re-entry guard so a fresh extend can fire after the
      // jump, and clear the visible "Loading more…" indicator.
      isLoadingRef.current = false;
      setIsExtending(false);
      // Any pending anchor snapshot is now stale.
      pendingPrependRef.current = null;
      pendingTopTrimRef.current = null;
      const from = clampToFloor(
        addDays(date, -initialBackDays),
        minDateRef.current,
      );
      const to = addDays(date, initialForwardDays);
      // A window that starts above the floor has days behind it that only
      // a prepend can reach — and this jump IS the interaction, so the
      // gate opens and the sentinel is re-observed once the window
      // commits. A window at the floor has nothing behind it: keep the
      // gate closed so the fresh window is not top-extended before the
      // user touches it.
      const floor = minDateRef.current ? toMidnight(minDateRef.current) : null;
      const startsAboveFloor = !floor || from.getTime() > floor.getTime();
      hasScrolledRef.current = startsAboveFloor;
      rearmTopRef.current = startsAboveFloor;
      setVisibleDays(
        rangeDays(from, to.getTime() < from.getTime() ? from : to),
      );
    },
    [initialBackDays, initialForwardDays],
  );

  return {
    visibleDays,
    topSentinelRef,
    bottomSentinelRef,
    isExtending,
    jumpTo,
  };
}
